import { bcStates } from 'common/common/states';
import { bcAuthEvents } from 'common/common/constants/auth-events';
import * as url from 'url';
import { BcAuth } from 'common/auth/auth.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { IdentityLoginConfigurationModel } from 'common/api/models/identity-login-configuration.model';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { StatusCodes } from 'http-status-codes';
import { WindowService } from 'shared/services/window/window.service';
import { DynamicQueryManagerService } from 'common/dynamicquery/dynamic-query-manager.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { CookieService } from 'ngx-cookie-service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthenticationType, FailureEvents, SuccessEvents } from 'common/auth/models/authentication-type';
import {
  AuthenticationResult,
  AuthenticationResultFailure,
  AuthenticationResultSuccess
} from 'common/auth/models/authentication-result';
import { first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class Bold360Service {
  constructor(private bcAuth: BcAuth, private dynamicQueryManager: DynamicQueryManagerService,
              private bcAnalytics: BcAnalytics, private $cookies: CookieService,
              private $window: WindowService,
              private rxEmitter: RxEmitter,
              private http: HttpClient) {
  }

  async init(isSSO: boolean, activatedRouteSnapshot: ActivatedRouteSnapshot): Promise<AuthenticationResult> {
    if (this.isConnected()) {
      return new AuthenticationResultSuccess();
    }

    let authenticationType: AuthenticationType = this.bcAuth.isLoggedIn() ? AuthenticationType.RECONNECT : null;

    if (!authenticationType) {
      authenticationType = await this.retakeSession(activatedRouteSnapshot, isSSO);
    }

    if (authenticationType === AuthenticationType.SSO) {
      // Legacy SSO of Admin console authenticates through WW
      const params = this.getStateParameters(activatedRouteSnapshot);
      if (params.accountId) {
        try {
          const returnUrl = await this.bcAuth.processReturnUrl(params.accountId);
          if (returnUrl) {
            return new AuthenticationResultFailure(returnUrl);
          }
        } catch (e) {
          // ignore return url related error and continue the login flow
        }
      }
    }

    if (!authenticationType && isSSO) {
      return new AuthenticationResultFailure(await this.loginWithSSO(activatedRouteSnapshot));
    }

    if (!authenticationType && await this.shouldShowInternalLoginScreen(activatedRouteSnapshot)) {
      return new AuthenticationResultFailure(null, bcStates.login.url);
    }

    if (!authenticationType) {
      try {
        authenticationType = await this.loginWithTicket(activatedRouteSnapshot);
        this.bcAuth.reloadAfterLoginIfNeeded();
      } catch (error) {
        if (error.status === StatusCodes.UNAUTHORIZED && error.error && error.error[0] && error.error[0].code === bcEnums.loginErrorCodes.IDENTITY_VALIDATION_FAILED) {
          return new AuthenticationResultFailure(error.error[0].details);
        }
      }
    }

    if (!authenticationType) {
      const identityLoginConfiguration = await this.bcAuth.getIdentityLoginConfiguration();
      return new AuthenticationResultFailure(identityLoginConfiguration.loginRedirectUrl);
    }

    const success = await this.bcAuth.prepareFrameworkAfterLogin(authenticationType);
    if (!success) {
      return new AuthenticationResultSuccess(null, true);
    }

    try {
      await this.connectDynamicQuery();
    } catch (error) {
      this.sendAnalyticsLogoutEvent(authenticationType);
      return new AuthenticationResultFailure();
    }

    this.rxEmitter.send(bcAuthEvents.loginSuccess, true);
    this.sendAnalyticsLoginEvent(authenticationType);
    if (isSSO || this.getIdentityTicket(activatedRouteSnapshot)) {
      return new AuthenticationResultSuccess(bcStates.layout.url);
    }

    return new AuthenticationResultSuccess();
  }

  canActivateRoute(): boolean {
    return this.isConnected();
  }

  isLoggedIn(): boolean {
    return this.bcAuth.isLoggedIn();
  }

  logout(reason?: string | { token: string }, returnUrl?: string): Promise<void> {
    return this.bcAuth.logout(reason, returnUrl);
  }

  async silentLogout(reason?: string | { token: string }): Promise<void> {
    if (this.bcAuth.isLoggedIn()) {
      return this.bcAuth.logoutWorker(reason);
    }
  }

  async isMultiAccount(): Promise<boolean> {
    return (await this.bcAuth.getUserAccountCount()) > 1;
  }

  canChangePassword(): boolean {
    return !this.bcAuth.isSSO;
  }

  getUserName(): string {
    return this.bcAuth.getSessionUserName();
  }

  async redeemAccessToken(): Promise<AuthenticationResult> {
    const identityLoginConfig = await this.bcAuth.getIdentityLoginConfiguration();
    return new AuthenticationResultFailure(identityLoginConfig.genesysCloudOauthRedirectUrl);
  }

  async isUserPaired(accessToken: string): Promise<boolean> {
    const identityLoginConfig = await this.bcAuth.getIdentityLoginConfiguration();
    const url = identityLoginConfig.loginRedirectUrl.replace(/v1\/login(.*)$/, 'v1/oauth/me');
    return this.http.get<{ hybrid: boolean }>(url, {
      params: {
        accessToken
      }
    })
      .pipe(
        map(response => response.hybrid)
      )
      .toPromise();
  }

  private sendAnalyticsLoginEvent(authenticationType: AuthenticationType) {
    if (authenticationType !== AuthenticationType.RECONNECT) {
      this.bcAnalytics.eventTrack(SuccessEvents[authenticationType], { language: this.bcAuth.getCurrentLanguage() });
    }
  }

  private sendAnalyticsLogoutEvent(authenticationType: AuthenticationType) {
    this.bcAnalytics.eventTrack('Logout', { from: FailureEvents[authenticationType] });
  }

  private isConnected(): boolean {
    return this.dynamicQueryManager.isConnected();
  }

  private async retakeSession(activatedRouteSnapshot: ActivatedRouteSnapshot, isSSO: boolean): Promise<AuthenticationType> {
    try {
      const requestParams = this.getStateParameters(activatedRouteSnapshot);
      await this.bcAuth.initForUser(requestParams);
      const isAuthenticated = await this.bcAuth.isAuthenticated();

      if (!isAuthenticated) {
        return null;
      }

      this.bcAuth.setSSO(isSSO);

      return isSSO ? AuthenticationType.SSO : AuthenticationType.REMEMBER_ME;
    } catch (err) {
      if (isSSO) {
        console.warn('SSO login could not be established: ', err);
      } else {
        console.warn('Remember me could not be established: ', err);
      }
    }
    return null;
  }

  private async loginWithSSO(activatedRouteSnapshot: ActivatedRouteSnapshot): Promise<string> {
    const requestParams = this.getStateParameters(activatedRouteSnapshot);
    const serverEndpoints = await this.bcAuth.initForUser(requestParams);
    const ssoProxyUrlBase = this.getSsoProxyUrl(serverEndpoints.ssoProxyEndpoint.url, requestParams);

    const failureUrl = this.generateFailureUrl(requestParams);
    const returnTo = this.generateReturnToUrl(serverEndpoints.accountId, serverEndpoints.restApiEndpoint.url, this.$window.location.href, failureUrl);
    const failureUrlForTheRedirect = this.generateFailureUrl(requestParams);
    const csrfToken = this.$cookies.get('csrftoken');

    return this.generateRedirectUserToUrl(ssoProxyUrlBase, returnTo, failureUrlForTheRedirect, 'proxy', csrfToken);
  }

  private async loginWithTicket(routeSnapshot: ActivatedRouteSnapshot): Promise<AuthenticationType> {
    const identityTicket = this.getIdentityTicket(routeSnapshot);
    if (identityTicket) {
      const accountId = this.getQueryParam('account-id', routeSnapshot);
      await this.bcAuth.validateIdentityTicket(accountId, identityTicket);
      return AuthenticationType.TICKET;
    }
    return null;
  }

  private async shouldShowInternalLoginScreen(routeSnapshot: ActivatedRouteSnapshot): Promise<boolean> {
    if (this.bcAuth.isIdentityLoginAllowed(routeSnapshot)) {
      const identityLoginConfiguration: IdentityLoginConfigurationModel = await this.bcAuth.getIdentityLoginConfiguration();
      return !identityLoginConfiguration.enabled;
    }

    return true;
  }

  private getIdentityTicket(routeSnapshot: ActivatedRouteSnapshot): string {
    return this.getQueryParam('ticket', routeSnapshot);
  }

  private async connectDynamicQuery(): Promise<void> {
    const isConnected = await this.dynamicQueryManager.connect().pipe(first()).toPromise();
    if (!isConnected) {
      console.warn('WebSocket connection could not be established');
      throw new Error('WebSocket connection could not be established');
    }
  }

  private generateRedirectUserToUrl(webApiUrl, returnTo, failureUrl, ssoType, csrfToken) {
    return `${webApiUrl}?ssotype=${ssoType}&redirecturl=${returnTo}&failureurl=${failureUrl}&_csrf=${csrfToken}`;
  }

  private generateReturnToUrl(accountId, restApiEndpoint, redirectTo, ssoFailureUrl) {
    const redirectToEncoded = encodeURIComponent(redirectTo);
    const ssoFailureUrlEncoded = encodeURIComponent(ssoFailureUrl);
    return encodeURIComponent(`${restApiEndpoint}/aid/${accountId}/v1/authentication/login-saml?redirecturl=${redirectToEncoded}&failureurl=${ssoFailureUrlEncoded}`);
  }

  private generateFailureUrl(requestParams) {
    let queryString = '';
    if (requestParams && requestParams.accountId) {
      queryString = `?aid=${requestParams.accountId}`;
    } else if (requestParams && requestParams.username) {
      queryString = `?un=${requestParams.username}`;
    }
    return url.format({
      protocol: this.$window.location.protocol,
      hostname: this.$window.location.hostname,
      port: this.$window.location.port,
      pathname: 'sso-error',
      search: queryString
    });
  }

  private getSsoProxyUrl(ssoProxyEndpointUrl, requestParams) {
    if (requestParams && requestParams.accountId) {
      return `${ssoProxyEndpointUrl}/aid/${requestParams.accountId}/`;
    } else if (requestParams && requestParams.username) {
      return `${ssoProxyEndpointUrl}/un/${requestParams.username}/`;
    } else {
      throw new Error('AccountId or username is missing for sso proxy');
    }
  }

  private getStateParameters(routeSnapshot: ActivatedRouteSnapshot): { accountId?: string, username?: string } {
    const requestParams: { accountId?: string, username?: string } = {};
    if (routeSnapshot) {
      if (routeSnapshot.queryParams) {
        const aidParam = routeSnapshot.queryParams['account-id'];
        if (aidParam) {
          requestParams.accountId = aidParam;
        }
      }
      if (!requestParams.accountId && routeSnapshot.paramMap) {
        if (routeSnapshot.paramMap.get('paramType') === 'account-id') {
          requestParams.accountId = routeSnapshot.paramMap.get('paramValue');
        } else if (routeSnapshot.paramMap.get('paramType') === 'username') {
          requestParams.username = routeSnapshot.paramMap.get('paramValue');
        }
      }
    }
    return requestParams;
  }

  private getQueryParam(param: string, routeSnapshot: ActivatedRouteSnapshot): string {
    return routeSnapshot.queryParams[param] || new URLSearchParams(this.$window.location.search).get(param);
  }
}
